/**
 * 1.8.3 (D-1.8.3-5) — the services the office adds, on the screens: the names (a built-in one through i18n, an added
 * one by the two names the office typed), the list as the app reads it, and the Services card with the six and the
 * added ones as ticks. A retired service is offered to nobody new and still reads, by name, where it already is.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ServiceKey } from '@shared/volunteers';
import { isBuiltInService, type AddedService } from '@shared/volunteer-services';
import { Icon } from '@/components/Icons';
import { apiFetch } from '@/lib/api';

/** The office's added services (retired ones too). Read by Book → Volunteer help, Add volunteer and Settings. */
export function useAddedServices(enabled = true) {
  return useQuery({ queryKey: ['volunteer-services'], queryFn: () => apiFetch<{ services: AddedService[] }>('/api/volunteers/services'), staleTime: 30_000, enabled });
}

/** A service in the screen's language: one of the six by its own words, an added one by the name the office typed. */
export function serviceLabel(key: string, t: (k: string) => string, lang: string, list: readonly AddedService[] | undefined): string {
  if (isBuiltInService(key)) return t(`vol.service.${key}`);
  const s = (list ?? []).find((x) => x.key === key);
  return s ? (lang === 'es' ? s.es : s.en) : key;
}

/**
 * The services this volunteer does, read only (22 Oct 2026, D-1022-TIDY): the card has no Save of its own — Edit on the
 * identity bar is the one place to change services (the six and the office's added ones; each change audited).
 */
export function ServicesCard183({ services, added, list }: { services: ServiceKey[]; added: string[]; list: readonly AddedService[] }) {
  const { t, i18n } = useTranslation();
  const uid = useId();
  const shown: string[] = [...services, ...added];
  return (
    <section aria-labelledby={`${uid}-h`} data-testid="services-card">
      <h3 className="eyebrow" id={`${uid}-h`}>
        {t('vol171.services.title')}
      </h3>
      {shown.length ? (
        <ul className="checklist vp-svc" aria-labelledby={`${uid}-h`}>
          {shown.map((s) => (
            <li key={s} data-testid={`svc-${s}`}>
              <Icon name="check" />
              <span>{serviceLabel(s, t, i18n.language, list)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="help" data-testid="svc-none">
          {t('vol1022tidy.noServices')}
        </p>
      )}
      <p className="help">{t('vol1022tidy.servicesHelp')}</p>
    </section>
  );
}
